import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Divider, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog, DialogModal, Drawer } from "@zfp-akademie-a-s/zfp-design";
import { FormCheckbox, FormSelect, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { type FinancialAssetFormType, getFinancialAssetFormSchema } from "Forms/Schemas/Assets/financialAssetFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import {
  getGetAssetsOverviewQueryKey,
  getGetFinancialAssetItemQueryKey,
  getGetFinancialProductExpenseQueryKey,
  getListFinancialAssetItemsQueryKey,
  useCreateFinancialAssetItem,
  useDeleteAssetItem,
  useGetAnalysisClients,
  useGetFinancialAssetItem,
  useGetFinancialProductExpense,
  useUpdateFinancialAssetItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type FinancialAssetItem, FinancialAssetItemFinancialSubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { FINANCIAL_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";

type Props = {
  readonly onClose: () => void;
  readonly financialAsset?: FinancialAssetItem;
  readonly open: boolean;
};

export const FinancialAssetDrawer: FC<Props> = ({ onClose, open, financialAsset }) => {
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const queryClient = useQueryClient();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: financialAssetDetail } = useGetFinancialAssetItem(analysisId, versionId, financialAsset?.guid || "", {
    query: { enabled: !!financialAsset?.guid },
  });
  const { data: financialProductExpense } = useGetFinancialProductExpense(analysisId, versionId);
  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getListFinancialAssetItemsQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getGetFinancialProductExpenseQueryKey(analysisId, versionId) });
  };

  const { mutate: create } = useCreateFinancialAssetItem();
  const { mutate: update } = useUpdateFinancialAssetItem();
  const { mutate: deleteItem } = useDeleteAssetItem();

  const methods = useForm<FinancialAssetFormType>({
    resolver: yupResolver(getFinancialAssetFormSchema(t)),
    mode: "onBlur",
  });

  const { control, handleSubmit, reset, watch } = methods;

  // biome-ignore lint: <Open is required.>
  useEffect(() => {
    const defaultValues: Partial<FinancialAssetFormType> = {
      financialAssetType: financialAssetDetail?.financialSubType,
      name: financialAssetDetail?.name,
      value: financialAssetDetail?.amount.value,
      note: financialAssetDetail?.note ?? undefined,
      institution: financialAssetDetail?.institution ?? undefined,
      clientId: financialAssetDetail?.clientGuid ?? undefined,
      expenseItemId: financialAssetDetail?.relatedExpenseItemGuid ?? undefined,
      isUsableForGoals: financialAssetDetail?.isUsableForGoals === undefined ? true : financialAssetDetail?.isUsableForGoals,
    };
    reset(defaultValues);
  }, [financialAssetDetail, open, reset]);

  const financialAssetOptions: Option[] = (
    Object.keys(FinancialAssetItemFinancialSubType) as Array<FinancialAssetItemFinancialSubType>
  ).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.Assets.Financial.FinancialAssetType[FINANCIAL_SUBTYPE_RESOURCE_KEY[value]]),
  }));

  const financialExpenseOptions: Option[] = [
    { value: "", name: "-" },
    ...(financialProductExpense?.items.map((expense) => ({
      value: expense.guid || "",
      name: `${expense.name}${expense.institution ? ` - ${expense.institution}` : ""} - ${formatCurrency(expense.amount.value, expense.amount.currencyCode)}/${t(Resources.Common.Month)}`,
      hidden: !(financialAssetDetail?.relatedExpenseItemGuid === expense.guid || !expense.relatedAssetItemGuid),
    })) || []),
  ];

  const clientOptions: Option[] = [
    { value: "", name: "-" },
    ...(clientsData?.clients.map((client) => ({
      value: client.guid,
      name: formatClientName(client.firstName, client.surname, client.birthDate ?? null),
    })) || []),
  ];

  const onSubmit = (data: FinancialAssetFormType) => {
    try {
      const payload = {
        amount: {
          currencyCode: "CZK",
          frequency: AmountFrequency.one_time,
          value: data.value,
        },
        note: data.note || "",
        name: data.name,
        institution: data.institution || "",
        financialSubType: data.financialAssetType as FinancialAssetItemFinancialSubType,
        clientGuid: data.clientId || null,
        relatedExpenseItemGuid: data.expenseItemId || null,
        isUsableForGoals: !!data.isUsableForGoals,
      };

      if (!financialAsset) {
        create(
          { analysisId, versionId, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Financial.Created));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error)),
          },
        );
      } else {
        update(
          { analysisId, versionId, itemId: financialAsset.guid || "", data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Financial.Edited));
              invalidate();
              queryClient.invalidateQueries({
                queryKey: getGetFinancialAssetItemQueryKey(analysisId, versionId, financialAsset.guid || ""),
              });
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error)),
          },
        );
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error));
    }
  };

  const onDelete = (relatedExpenseItemGuid?: string) => {
    try {
      deleteItem(
        {
          analysisId,
          versionId,
          itemId: financialAsset?.guid || "",
          params: relatedExpenseItemGuid ? { relatedExpenseItemGuid } : undefined,
        },
        {
          onSuccess: () => {
            toast.success(t(Resources.AnalysisDetail.Assets.Financial.Deleted));
            invalidate();
            onClose();
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error)),
        },
      );

      setDeleteModalOpen(false);
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error));
    }
  };

  const financialAssetType = watch("financialAssetType");

  return (
    <>
      <FormProvider {...methods}>
        <Drawer
          open={open}
          onClose={onClose}
          closeLabel={t(Resources.Common.Close)}
          title={financialAsset ? t(Resources.AnalysisDetail.Assets.Financial.Edit) : t(Resources.AnalysisDetail.Assets.Financial.Add)}
          onSubmit={handleSubmit(onSubmit)}
          startAction={
            !!financialAsset && (
              <Button variant="text" color="error" disabled={readOnly} onClick={() => setDeleteModalOpen(true)}>
                {t(Resources.Common.Delete)}
              </Button>
            )
          }
          primaryAction={
            <Button variant="contained" type="submit" disabled={readOnly}>
              {financialAsset ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
            </Button>
          }
        >
          <Grid container spacing={Spacing.XL} py={Spacing.XXL}>
            <Grid size={12}>
              <FormSelect
                control={control}
                name="financialAssetType"
                label={t(Resources.AnalysisDetail.Assets.Financial.FinancialAssetType.Title)}
                required
                options={toSelectOptions(financialAssetOptions)}
                placeholder={t(Resources.Common.SelectPlaceholder)}
                disabled={readOnly}
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <FormTextField
                control={control}
                name="name"
                label={t(Resources.AnalysisDetail.Assets.Name)}
                required
                disabled={readOnly}
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <FormNumberField
                control={control}
                name="value"
                label={t(Resources.AnalysisDetail.Assets.Financial.Value)}
                required
                scale={0}
                min={0}
                thousandsSeparator=" "
                unit={t(Resources.Common.CZKSuffix)}
                disabled={readOnly}
                placeholder="0"
                fullWidth
              />
            </Grid>
            {financialProductExpense?.entryMode === "individual" &&
              !([FinancialAssetItemFinancialSubType.cash, FinancialAssetItemFinancialSubType.bank_account] as string[]).includes(
                financialAssetType,
              ) && (
                <Grid size={12}>
                  <FormSelect
                    control={control}
                    name="expenseItemId"
                    label={t(Resources.AnalysisDetail.Assets.Financial.RegisteredExpenses)}
                    options={toSelectOptions(financialExpenseOptions)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    disabled={readOnly}
                    fullWidth
                  />
                </Grid>
              )}

            <Grid size={6}>
              <FormTextField
                control={control}
                name="institution"
                label={t(Resources.AnalysisDetail.Assets.Financial.Insitution)}
                disabled={readOnly}
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <FormSelect
                control={control}
                name="clientId"
                label={t(Resources.AnalysisDetail.Assets.Financial.BelongsTo)}
                options={toSelectOptions(clientOptions)}
                placeholder={t(Resources.Common.SelectPlaceholder)}
                disabled={readOnly}
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <FormTextField
                control={control}
                name="note"
                label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                maxLength={NOTE_MAX_LENGTH}
                disabled={readOnly}
                multiline
                minRows={6}
                maxRows={8}
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <Divider />
            </Grid>
            <Grid size={12}>
              <Typography fontWeight={600} variant="subtitle1" mb={Spacing.XL}>
                {t(Resources.AnalysisDetail.Assets.Attributes)}
              </Typography>
              <FormCheckbox
                control={control}
                name="isUsableForGoals"
                label={t(Resources.AnalysisDetail.Assets.IsUsableForGoals)}
                disabled={readOnly}
              />
            </Grid>
          </Grid>
        </Drawer>
      </FormProvider>
      {financialAssetDetail?.relatedExpenseItemGuid ? (
        <DialogModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          closeLabel={t(Resources.Common.Close)}
          title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseItem)}
          subtitle={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteLoanExpenseItemWithoutAssetHint)}
          secondaryAction={
            <Button variant="contained" onClick={() => onDelete(financialAssetDetail.relatedExpenseItemGuid ?? undefined)}>
              {t(Resources.AnalysisDetail.Assets.Liabilities.DeleteLiabilityWithLoanExpense)}
            </Button>
          }
          primaryAction={
            <Button variant="contained" onClick={() => onDelete()}>
              {t(Resources.AnalysisDetail.Assets.Liabilities.DeleteLiabilityWithoutLoanExpense)}
            </Button>
          }
        />
      ) : (
        <ConfirmDialog
          open={deleteModalOpen}
          title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseItem)}
          message={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteLoanExpenseItemWithoutAssetHint)}
          confirmLabel={t(Resources.Common.Yes)}
          cancelLabel={t(Resources.Common.Cancel)}
          onConfirm={() => onDelete()}
          onCancel={() => setDeleteModalOpen(false)}
        />
      )}
    </>
  );
};
